import React, { createContext, useContext, useState, useCallback } from 'react';
import { creditsApi } from '../lib/api/credits';
import type { CreditTransactionResponse } from '../types/credits';

interface CreditsContextType {
  credits: number;
  updateCredits: (userId: string) => Promise<void>;
  deductCredits: (userId: string, taskId: string, amount: number, description: string, metadata?: Record<string, any>) => Promise<CreditTransactionResponse>;
  refundCredits: (transactionId: string, amount?: number, reason?: string) => Promise<CreditTransactionResponse>;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

export function CreditsProvider({ children }: { children: React.ReactNode }) {
  const [credits, setCredits] = useState(0);

  const updateCredits = useCallback(async (userId: string) => {
    const response = await creditsApi.getCurrentCredits(userId);
    if (response.success && response.available_credits !== undefined) {
      setCredits(response.available_credits);
    }
  }, []);

  const deductCredits = useCallback(async (
    userId: string,
    taskId: string,
    amount: number,
    description: string,
    metadata?: Record<string, any>
  ) => {
    const response = await creditsApi.deductCredits({
      userId,
      taskId,
      amount,
      description,
      metadata
    });

    if (response.success && response.remaining_credits !== undefined) {
      setCredits(response.remaining_credits);
    }

    return response;
  }, []);

  const refundCredits = useCallback(async (
    transactionId: string,
    amount?: number,
    reason?: string
  ) => {
    const response = await creditsApi.refundCredits({
      transactionId,
      amount,
      reason
    });

    if (response.success && response.available_credits !== undefined) {
      setCredits(response.available_credits);
    }

    return response;
  }, []);

  return (
    <CreditsContext.Provider value={{ 
      credits, 
      updateCredits, 
      deductCredits,
      refundCredits
    }}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  const context = useContext(CreditsContext);
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
} 